import React from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Typography } from '@mui/material';
import { add, addMinutes, endOfDay, endOfMonth, endOfWeek, format, isValid, startOfDay, startOfMonth, startOfWeek, sub } from 'date-fns';
import { epochDateString, PeriodBehavior, RelativeDatePeriod, RelativeDateTitle, RelativeDateType } from './AvDateRangePicker.constants';
import type { DateRangeValueType, DateValue, PredefinedPreset, Preset } from './types';

export const isDateRangeEmpty = (value: DateRangeValueType): value is 'empty' => value === 'empty';
export const isDateRangeNotEmpty = (value: DateRangeValueType): value is 'notEmpty' => value === 'notEmpty';
export const isDateRangeValue = (value: DateRangeValueType): value is DateValue =>
  Boolean(value && !isDateRangeEmpty(value) && !isDateRangeNotEmpty(value) && typeof value === 'object' && 'from' in value);

export const customPreset = 'Custom';
export const dateFormat = 'MMM dd, yyyy';
export const dateTimeFormat = 'MMM dd, yyyy, hh:mm a';
export const shortDateTimeFormat = 'MMM dd, hh:mm aaa';

export enum RelativeUnit {
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
}

export enum RadioButtonValue {
  relative = 'relative',
  empty = 'empty',
  notEmpty = 'notEmpty',
}

export const getStartOfDay = (date = new Date()) => startOfDay(date);
export const getEndOfDay = (date = new Date()) => endOfDay(date);
export const getStartOfWeek = (date = new Date()) => startOfWeek(date, { weekStartsOn: 1 });
export const getStartUtc = (date: Date) => new Date(`${date.toISOString().split('T')[0]}T00:00:00.000Z`);
export const getEndUtc = (date: Date) => new Date(`${date.toISOString().split('T')[0]}T23:59:59.999Z`);
export const addLocalTimeChange = (date: Date) => new Date(addMinutes(date, -date.getTimezoneOffset()));
export const changeToLocal = (date: Date) => new Date(date.toISOString().split('Z')[0]);

export const fromDateMapping = {
  [PeriodBehavior.next]: Object.fromEntries(Object.keys(RelativeUnit).map(key => [key, () => getStartOfDay()])),
  [PeriodBehavior.last]: {
    [RelativeUnit.MINUTES]: (minutes: number) => sub(new Date(), { minutes }),
    [RelativeUnit.HOURS]: (hours: number) => sub(new Date(), { hours }),
    [RelativeUnit.DAYS]: (days: number) => sub(getStartOfDay(), { days: days - 1 }),
    [RelativeUnit.WEEKS]: (weeks: number) => sub(getStartOfWeek(), { weeks: weeks - 1 }),
    [RelativeUnit.MONTHS]: (months: number) => startOfMonth(sub(new Date(), { months: months - 1 })),
  },
  [PeriodBehavior.previous]: {
    [RelativeUnit.MINUTES]: (minutes: number) => sub(new Date(), { minutes }),
    [RelativeUnit.HOURS]: (hours: number) => sub(new Date(), { hours }),
    [RelativeUnit.DAYS]: (days: number) => startOfDay(sub(getStartOfDay(), { days })),
    [RelativeUnit.WEEKS]: (weeks: number) => getStartOfWeek(sub(getStartOfDay(), { weeks })),
    [RelativeUnit.MONTHS]: (months: number) => startOfMonth(sub(getStartOfDay(), { months })),
  },
  [PeriodBehavior.before]: Object.fromEntries(Object.keys(RelativeUnit).map(key => [key, () => new Date(epochDateString)])),
};

export const toDateMapping = {
  [PeriodBehavior.next]: Object.fromEntries(
    Object.keys(RelativeUnit).map(key => [key, (count: number) => add(getStartOfDay(), { [key.toLowerCase()]: count })])
  ),
  [PeriodBehavior.last]: Object.fromEntries(Object.keys(RelativeUnit).map(key => [key, () => getEndOfDay()])),
  [PeriodBehavior.previous]: {
    [RelativeUnit.MINUTES]: () => sub(new Date(), { minutes: 1 }),
    [RelativeUnit.HOURS]: () => sub(new Date(), { hours: 1 }),
    [RelativeUnit.DAYS]: () => getEndOfDay(sub(getStartOfDay(), { days: 1 })),
    [RelativeUnit.WEEKS]: () => endOfWeek(getStartOfWeek(sub(getStartOfDay(), { weeks: 1 })), { weekStartsOn: 1 }),
    [RelativeUnit.MONTHS]: () => endOfMonth(sub(getStartOfDay(), { months: 1 })),
  },
  [PeriodBehavior.before]: {
    [RelativeUnit.MINUTES]: (minutes: number) => sub(new Date(), { minutes }),
    [RelativeUnit.HOURS]: (hours: number) => sub(new Date(), { hours }),
    [RelativeUnit.DAYS]: (days: number) => getEndOfDay(sub(getStartOfDay(), { days })),
    [RelativeUnit.WEEKS]: (weeks: number) => endOfWeek(getStartOfWeek(sub(getStartOfDay(), { weeks })), { weekStartsOn: 1 }),
    [RelativeUnit.MONTHS]: (months: number) => endOfMonth(sub(getStartOfDay(), { months })),
  },
};

export const getUtcRange = (from: Date, to: Date) => {
  const startUtc = getStartUtc(addLocalTimeChange(from));
  const endUtc = getEndUtc(addLocalTimeChange(to));
  return [startUtc, endUtc];
};

export const getLocalRange = (from: Date, to: Date) => [changeToLocal(from), changeToLocal(to)];

export const formatDate = (date: Date | string | undefined, token: string): string => {
  if (!date) {
    return '';
  }

  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  return isValid(parsedDate) ? format(parsedDate, token) : '';
};

export const stringDefinition = definition => definition && `${definition.type}-${definition.count}-${definition.period}`;

export const validateValue = (date: DateRangeValueType, isRange: boolean): DateRangeValueType =>
  isRange
    ? {
        from: isDateRangeValue(date) ? new Date(date.from) : getStartOfDay(),
        to: isDateRangeValue(date) && date?.to ? new Date(date.to) : getEndOfDay(),
      }
    : date || { from: getStartOfDay() };

export const lastDaysPreset = count => ({
  title: `Last ${count} Days`,
  definition: { type: RelativeDateType.last, count, period: RelativeDatePeriod.days },
});
export const nextDaysPreset = count => ({
  title: `Next ${count} Days`,
  definition: { type: RelativeDateType.next, count, period: RelativeDatePeriod.days },
});

export const getPresets = ({ allowFutureDates }): PredefinedPreset[] => {
  const presets = [lastDaysPreset(7), lastDaysPreset(14), lastDaysPreset(30)];

  presets.push(
    {
      title: RelativeDateTitle.LastMonth,
      definition: { type: RelativeDateType.last, count: 1, period: RelativeDatePeriod.months },
    },
    {
      title: RelativeDateTitle.LastThreeMonths,
      definition: { type: RelativeDateType.last, count: 3, period: RelativeDatePeriod.months },
    }
  );

  if (allowFutureDates) {
    presets.push(nextDaysPreset(7), nextDaysPreset(14), nextDaysPreset(30));
  }

  return presets;
};

export const getInputValue = ({
  date,
  isRange,
  timeOptions,
  preset = {},
  customFormat = '',
}: {
  date: DateRangeValueType;
  isRange: boolean;
  timeOptions?: any[];
  preset?: Preset;
  customFormat?: string;
}) => {
  const format = customFormat || (timeOptions ? dateTimeFormat : dateFormat);
  return isDateRangeValue(date)
    ? isRange
      ? preset.type === RelativeDateType.before
        ? `Before ${date.to && formatDate(add(date.to, { days: 1 }), format)}`
        : `${formatDate(date.from, format)} - ${formatDate(date.to, format)}`
      : formatDate(date.from, format)
    : undefined;
};

const getFromDate = (definition?: Preset): Date => {
  const { type, period, count } = definition || {};

  if (period && count && type) {
    return fromDateMapping[type.toUpperCase()][period.toUpperCase()]?.(count);
  }

  return getStartOfDay();
};

const getToDate = (definition?: Preset): Date => {
  const { period, type, count } = definition || {};

  if (period && [RelativeDatePeriod.hours, RelativeDatePeriod.minutes].includes(period)) {
    return new Date();
  }

  if (period && count && type) {
    return toDateMapping[type.toUpperCase()][period.toUpperCase()](count);
  }

  return getStartOfDay();
};

export const getDatesFromDefinition = (definition?: Preset) => ({
  from: getFromDate(definition),
  to: getToDate(definition),
});

export const captionComponent = ({ displayMonth }: { displayMonth: Date }) => (
  <Typography variant="h7" sx={{ color: theme => theme.palette.colors.neutrals[600] }}>
    {format(displayMonth, 'MMM yyyy')}
  </Typography>
);

export const DatePickerStartAdornment = ({ label, icon, tooltipText }: { label?: string; icon?: any; tooltipText?: string }) => (
  <AvTooltip title={tooltipText}>
    <Box
      className="ofir"
      sx={{
        ...flex.itemsCenter,
        borderRadius: '8px',
        gap: 1,
        background: theme => theme.palette.colors.primary[150],
        svg: { fill: theme => theme.palette.colors.primary[500] },
        py: '2px',
        pl: 1.5,
        pr: 2,
        pointerEvents: 'all',
        cursor: 'default',
      }}>
      {icon}
      {label}
    </Box>
  </AvTooltip>
);

export const isPresetValid = (preset: Preset | undefined) => {
  if (!preset) {
    return true;
  }

  const fields = [preset.type, preset.count, preset.period];

  return fields.every(field => !field) || fields.every(field => field);
};
